import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { TextLesson } from "../../components/TextLesson";
import { TextImgLesson } from "../../components/TextImgLesson";
import { VideoLesson } from "../../components/VideoLesson";
import { SurveyLesson } from "../../components/SurveyLesson";
import { AudioLesson } from "../../components/AudioLesson";
import { AnswerLesson } from "../../components/AnswerLesson";
import { RewardLesson } from "../../components/RewardLesson";
import { ILesson, ISection, TRewardType } from "../../type";
import { completeExercise } from "../../api/lesson";
import { LoadFileLesson } from "../../components/LoadFileLesson";
import "./index.css";
import { getTodayLesson, isCompletedLesson } from "../../utils/current_section";
import { Preloader } from "../../components/Preloader";
import { prefetchFromCurrentLesson } from "../../utils/offlineLessonPrefetch";

interface IProps {
  sections: ISection[];
  reload_lessons: () => void;
}

export function Lesson(props: IProps) {
  const { secId, lessonid } = useParams<{
    secId: string;
    lessonid?: string;
  }>();
  const nav = useNavigate();
  let [rewardType, setRewardType] = useState<TRewardType>('miss coin');
  let [rewardCoinCount, setRewardCoinCount] = useState<number>(0);
  let [page, setPage] = useState<number>(0);
  const [mediaReloadNonce, setMediaReloadNonce] = useState(0);
  const prefetchKeyRef = useRef<string>("");
  const sectionIdNum = Number(secId || 0);
  const paramLessonIdNum = Number(lessonid || 0);

  useEffect(() => {
    if (!Array.isArray(props.sections) || props.sections.length === 0) return;
    if (!Number.isFinite(sectionIdNum) || sectionIdNum <= 0) return;
    const sectionForPrefetch = props.sections.find((s) => Number(s.id) === sectionIdNum);
    if (!sectionForPrefetch) return;

    const resolvedLessonId = Number.isFinite(paramLessonIdNum) && paramLessonIdNum > 0
      ? paramLessonIdNum
      : Number(getTodayLesson(props.sections)?.id || 0);
    if (!Number.isFinite(resolvedLessonId) || resolvedLessonId <= 0) return;

    const key = `${sectionIdNum}:${resolvedLessonId}`;
    if (prefetchKeyRef.current === key) return;
    prefetchKeyRef.current = key;
    void prefetchFromCurrentLesson(props.sections, sectionIdNum, resolvedLessonId);
  }, [props.sections, sectionIdNum, paramLessonIdNum]);
  let section = props.sections.find(
    (item) => item.id === +(secId || 0)
  ) as ISection;

  // Если sections ещё не загрузились или нужный section не найден — не падаем.
  if (!props.sections || props.sections.length === 0) {
    return <Preloader />;
  }

  if (lessonid && !section) {
    return <Navigate to="/" replace />;
  }

  let lesson:ILesson;
  if (lessonid) {
    // section уже проверен выше
    lesson = section.Lesson_SectionRelation.find(
      (l) => l.id === +lessonid
    )!;
  } else {
    lesson = getTodayLesson(props.sections);
    if (!lesson) return <Preloader />;
  }

  const exercises = lesson.Exercise_LessonRelation;
  let sectionCl = "section mainPage flex flex-column lessonPageRoot";
  const length = exercises.length; //сколько заданий в текущем уроке
  let completed = isCompletedLesson(lesson.Exercise_LessonRelation);

  const safeJsonParse = (raw: unknown): Record<string, unknown> => {
    if (raw == null) return {};
    if (typeof raw === "object") return raw as Record<string, unknown>;
    const src = String(raw);

    const tryParse = (value: string) => {
      try {
        return JSON.parse(value) as Record<string, unknown>;
      } catch {
        return null;
      }
    };

    // 1) Raw parse
    const direct = tryParse(src);
    if (direct) return direct;

    // 2) Most common broken payload: trailing backslashes / CRLF-escaped fragments
    const normalized = src
      .replace(/\\</g, "<")
      .replace(/\\(?!["\\/bfnrtu])/g, "\\\\")
      .replace(/\r\n/g, "\\n")
      .replace(/\r/g, "\\n");

    const normalizedParsed = tryParse(normalized);
    if (normalizedParsed) return normalizedParsed;

    // 3) Try extracting known fields from "almost JSON" payloads.
    // Example broken payload:
    // {"description":"text\<br>\..."}
    const extractField = (name: string): string | null => {
      const re = new RegExp(`"${name}"\\s*:\\s*"([\\s\\S]*)"\\s*}\\s*$`);
      const m = src.match(re);
      if (!m || typeof m[1] !== "string") return null;
      return m[1]
        .replace(/\\</g, "<")
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, "\\")
        .replace(/\\r\\n/g, "\n")
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\n")
        .replace(/\\$/g, "");
    };

    const extractedTitle = extractField("titleLesson");
    const extractedDescription = extractField("description");
    if (extractedTitle !== null || extractedDescription !== null) {
      return {
        titleLesson: extractedTitle ?? "",
        description: extractedDescription ?? "",
      };
    }

    // 4) Fallback for legacy/plain text values
    return { titleLesson: src, description: src };
  };

  const getTitleLesson = (raw: unknown) => {
    const obj = safeJsonParse(raw);
    const v = obj.titleLesson;
    return typeof v === "string" ? v : "";
  };

  const getDescription = (raw: unknown) => {
    const obj = safeJsonParse(raw);
    const v = obj.description;
    return typeof v === "string" ? v : "";
  };

  const getSurveyQuestions = (raw: unknown) => {
    const obj = safeJsonParse(raw);
    const values = Object.values(obj).filter((v): v is string => typeof v === "string");
    return values;
  };

  //перейти на следующий урок
  //@passed - true - если нажали пропустить
  //@answer - ответ который ввели или выбрали
  const goToNextExercise = (passed?: boolean, answer?: string, noSendToServer?:boolean) => {
    //completed - все задания в этом уроке уже пройдены
    if (completed && length - 1 === page) { // если урок пройден и это последняя страница
      if (!noSendToServer) {
        // Дожидаемся подтверждения с сервера, чтобы статус "pass" сразу сменился на "done" в Main.
        completeExercise(exercises[page].id, passed, answer).finally(() => {
          props.reload_lessons();
          nav("/");
        });
      } else {
        props.reload_lessons();
        nav("/");
      }
    } else { 
      if (page === length) { //когда в награде нажали далее
        nav("/");
        props.reload_lessons();
      } else { // все остальные случаи
        if (!noSendToServer){
          completeExercise(exercises[page].id, passed, answer)
            .then((r) => {
              setRewardType(r.addCoin);
              setRewardCoinCount(r.value);
              setPage(page + 1);
              // Обновляем расписание всегда: иначе после выкупа пропущенного урока
              // и успешного прохождения иконка дня может остаться "pass" до ручной перезагрузки.
              props.reload_lessons();
            })
            .catch(() => {
              // apiFetch при потере сессии уводит на /login; иначе — не оставляем висящий промис
              props.reload_lessons();
            });
        }
        else
          setPage(page + 1); //листаем упражнение в уроке
      }
    }
  };

  // page = length; rewardType = 'Silver';

  if (length === page) {
    sectionCl = sectionCl + " ellipseImageSilver";
  }

  const showMediaReload = page < length;

  return (
    <section className={sectionCl}>
      {showMediaReload && (
        <button
          type="button"
          className="lessonPageForceReload"
          title="Обновить подкаст и медиа"
          aria-label="Обновить загрузку контента задания"
          onClick={() => setMediaReloadNonce((n) => n + 1)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M21 12a9 9 0 0 1-15.3 6.3M3 12a9 9 0 0 1 15.3-6.3M3 12V7m0 5h4.5M21 12v5m0-5h-4.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      <div className="progressBar">
        {exercises.map((p, index) => {
          let cls = "";
          if (index <= page) {
            cls = "active";
          }
          return <div className={cls} key={index} />;
        })}
      </div>
      {length === page ? ( //@ts-ignore
        rewardType === 'miss coin' ? <Navigate to={"/"}/> :
        <RewardLesson coin={rewardCoinCount} onClickNextEx={goToNextExercise} addCoin={rewardType} />
      ) : exercises[page].type_lesson === "text" ? (
        <TextLesson
          id={exercises[page].id}
          title={getTitleLesson(exercises[page].title_lesson)}
          content={getDescription(exercises[page].description)}
          onClickNextEx={goToNextExercise}
        />
      ) : exercises[page].type_lesson === "textImg" ? (
        <TextImgLesson
          key={`ex-${exercises[page].id}-${mediaReloadNonce}`}
          id={exercises[page].id}
          title={getTitleLesson(exercises[page].title_lesson)}
          content={getDescription(exercises[page].description)}
          url={exercises[page].url}
          cacheBust={mediaReloadNonce}
          onClickNextEx={goToNextExercise}
        />
      ) : exercises[page].type_lesson === "video" ? (
        <VideoLesson
          key={`ex-${exercises[page].id}-${mediaReloadNonce}`}
          id={exercises[page].id}
          url={exercises[page].url}
          cacheBust={mediaReloadNonce}
          onClickNextEx={goToNextExercise}
        />
      ) : exercises[page].type_lesson === "survey" ? (
        <SurveyLesson
          id={exercises[page].id}
          title={exercises[page].name_lesson}
          question={getSurveyQuestions(exercises[page].title_lesson)}
          onClickNextEx={goToNextExercise}
          answer={exercises[page].UserComplete_ExercisesRelation}
        />
      ) : exercises[page].type_lesson === "audio" ? (
        <AudioLesson
          key={`ex-${exercises[page].id}-${mediaReloadNonce}`}
          id={exercises[page].id}
          url={exercises[page].url}
          title_lesson={getTitleLesson(exercises[page].title_lesson)}
          author="Сергей Авструб"
          previwUrl={"/" + exercises[page].previwUrl}
          cacheBust={mediaReloadNonce}
          onClickNextEx={goToNextExercise}
        />
      ) : exercises[page].type_lesson === "answer" ? (
        <AnswerLesson
          id={exercises[page].id}
          title={getTitleLesson(exercises[page].title_lesson)}
          onClickNextEx={goToNextExercise}
          answer={exercises[page].UserComplete_ExercisesRelation}
        />
      ) : exercises[page].type_lesson === "loadFile" ? (
        <LoadFileLesson
          id={exercises[page].id}
          title={getTitleLesson(exercises[page].title_lesson)}
          content={getDescription(exercises[page].description)}
          onClickNextEx={goToNextExercise}
        />
      ) : (
        ""
      )}
    </section>
  );
}
